import { Link } from "react-router-dom";
import { useData } from "../context/DataContext";
import SEO from "../components/SEO";
import { useMobileOptimization } from "../utils/useMobileOptimization";

export default function Works() {
  const { works: exhibitions, loading, error } = useData();
  const { isMobile, getAnimationClass } = useMobileOptimization();

  if (error) {
    return (
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        <h2 className="text-xl font-normal m-0 mb-8 tracking-wide">Works</h2>
        <p>Error loading works: {error}</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Works"
        description="A curated selection of Nathan Portelli's exhibitions and projects showcasing contemporary artistic practice and creative exploration across various mediums and contexts."
        url="https://nathanportelli.com/works"
      />
      <div className="mt-10 ml-5 w-full flex flex-col animate-fadeInUp max-md:ml-0 max-md:w-full max-md:overflow-x-hidden">
        <h2 className="text-xl font-normal m-0 mb-8 tracking-wide">Works</h2>

        {loading ? (
          <div className="min-h-96 max-h-96 flex flex-col">
            <div className="flex flex-col items-center justify-center flex-1 min-h-96">
              <div className="flex gap-1 mb-5">
                <div
                  className="w-2 h-2 bg-black rounded-full animate-bounce"
                  style={{ animationDelay: "-0.32s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-black rounded-full animate-bounce"
                  style={{ animationDelay: "-0.16s" }}
                ></div>
                <div className="w-2 h-2 bg-black rounded-full animate-bounce"></div>
              </div>
              <p className="text-gray-600 text-sm m-0 tracking-wide">
                Loading works...
              </p>
            </div>
          </div>
        ) : exhibitions.length === 0 ? (
          <div
            className={`mt-10 max-w-4xl ${getAnimationClass("animate-fadeIn")}`}
          >
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No works available yet
              </h3>
              <p className="text-gray-500 text-sm max-w-md">
                Exhibition works and projects will be displayed here as they
                become available.
              </p>
            </div>
          </div>
        ) : (
          <div
            className={`grid grid-cols-2 gap-10 mt-10 max-w-4xl ${getAnimationClass(
              "animate-fadeIn"
            )} max-md:grid-cols-1 max-md:gap-8`}
          >
            {exhibitions.map((exhibition) => (
              <Link
                key={exhibition.id}
                to={`/works/${exhibition.id}`}
                className="no-underline text-inherit block"
              >
                <div
                  className={`border border-gray-200 bg-white transition-all duration-300 ${
                    !isMobile ? "hover:shadow-lg hover:-translate-y-1" : ""
                  } ${getAnimationClass("animate-fadeInScale")}`}
                >
                  <div className="w-full h-48 bg-gray-50 flex items-center justify-center">
                    {exhibition.headerImage ? (
                      <img
                        src={exhibition.headerImage}
                        alt={exhibition.title}
                        className="w-full h-full object-cover block"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                        Image
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-normal m-0 mb-4 tracking-wide leading-tight">
                      {exhibition.title}
                    </h3>
                    <div className="flex flex-col gap-2">
                      <span className="text-sm text-gray-600 tracking-wide font-medium">
                        {exhibition.year}
                      </span>
                      <span className="text-sm text-gray-600 tracking-wide italic">
                        {exhibition.location}
                      </span>
                      <span className="text-sm text-gray-600 tracking-wide mt-1">
                        Curated by {exhibition.curator}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
